"use client";
import { PrimitiveIcon, UIIcon } from "@gluestack-ui/core/icon/creator";
import type { VariantProps } from "@gluestack-ui/utils/nativewind-utils";
import {
  tva,
  useStyleContext,
  withStyleContext,
} from "@gluestack-ui/utils/nativewind-utils";
import { cssInterop } from "nativewind";
import React, { useMemo } from "react";
import { Text, View } from "react-native";

import { Svg } from "react-native-svg";
const SCOPE = "BADGE";

const badgeStyle = tva({
  base: "flex-row items-center data-[disabled=true]:opacity-50 gap-2",
  variants: {
    hidden: {
      true: "hidden",
      false: "",
    },
    action: {
      error: "bg-background-error border-error-300",
      warning: "bg-background-warning border-warning-300",
      success: "bg-background-success border-success-300",
      info: "bg-background-info border-info-300",
      muted: "bg-background-muted border-background-300",
      transparent: "bg-transparent border-background-700",
      brand: "bg-brand-100 border-brand-400",
      darkBrand: "bg-brand-400 border-brand-800",
    },
    variant: {
      solid: "",
      outline: "border",
    },
    size: {
      sm: "px-2 py-1",
      md: "px-2 py-1",
      lg: "px-3 py-2",
      xl: "px-3 py-2",
      "2xl": "px-6 py-2",
    },
    rounded: {
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl",
      "2xl": "rounded-2xl",
      "3xl": "rounded-3xl",
      "4xl": "rounded-4xl",
      full: "rounded-full",
    },
  },
});

const badgeTextStyle = tva({
  base: "text-typography-700 font-body font-normal tracking-normal",

  parentVariants: {
    action: {
      error: "text-error-800",
      warning: "text-warning-800",
      success: "text-success-800",
      info: "text-info-800",
      muted: "text-secondary-800",
      transparent: "text-secondary-800",
      brand: "text-brand-400",
      darkBrand: "text-typography-0",
    },
    size: {
      sm: "text-2xs",
      md: "text-xs",
      lg: "text-sm",
      xl: "text-lg",
      "2xl": "text-xl",
    },
  },
  variants: {
    isTruncated: {
      true: "web:truncate",
    },
    bold: {
      true: "font-bold",
    },
    underline: {
      true: "underline",
    },
    strikeThrough: {
      true: "line-through",
    },
    sub: {
      true: "text-xs",
    },
    italic: {
      true: "italic",
    },
    highlight: {
      true: "bg-yellow-500",
    },
  },
});

const badgeIconStyle = tva({
  base: "fill-none",
  parentVariants: {
    action: {
      error: "text-error-800",
      warning: "text-warning-800",
      success: "text-success-800",
      info: "text-info-800",
      muted: "text-secondary-800",
      brand: "text-brand-400",
      darkBrand: "text-typography-0",
    },
    size: {
      sm: "h-3 w-3",
      md: "h-3.5 w-3.5",
      lg: "h-4 w-4",
      xl: "h-5 w-5",
    },
  },
  variants: {
    hidden: {
      true: "hidden",
      false: "",
    },
    fillAction: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      action: "error",
      fillAction: true,
      class: "fill-error-800",
    },
    {
      action: "warning",
      fillAction: true,
      class: "fill-warning-800",
    },
    {
      action: "success",
      fillAction: true,
      class: "fill-success-800",
    },
    {
      action: "info",
      fillAction: true,
      class: "fill-info-800",
    },
    {
      action: "muted",
      fillAction: true,
      class: "fill-secondary-800",
    },
    {
      action: "brand",
      fillAction: true,
      class: "fill-brand-400",
    },
    {
      action: "darkBrand",
      fillAction: true,
      class: "fill-typography-0",
    },
  ],
});

const ContextView = withStyleContext(View, SCOPE);

cssInterop(PrimitiveIcon, {
  className: {
    target: "style",
    nativeStyleToProp: {
      height: true,
      width: true,
      fill: true,
      color: "classNameColor",
      stroke: true,
    },
  },
});

type IBadgeProps = React.ComponentPropsWithoutRef<typeof ContextView> &
  VariantProps<typeof badgeStyle>;
function Badge({
  children,
  action = "info",
  variant = "solid",
  size = "md",
  rounded = "md",
  hidden = false,
  className,
  ...props
}: { className?: string } & IBadgeProps) {
  const contextValue = useMemo(
    () => ({ action, variant, size, rounded }),
    [action, variant, size],
  );

  return (
    <ContextView
      className={badgeStyle({
        action,
        variant,
        class: className,
        size,
        rounded,
        hidden,
      })}
      {...props}
      context={contextValue}
    >
      {children}
    </ContextView>
  );
}

type IBadgeTextProps = React.ComponentPropsWithoutRef<typeof Text> &
  VariantProps<typeof badgeTextStyle>;

const BadgeText = React.forwardRef<
  React.ComponentRef<typeof Text>,
  IBadgeTextProps
>(function BadgeText({ children, className, size, bold, ...props }, ref) {
  const { size: parentSize, action: parentAction } = useStyleContext(SCOPE);
  return (
    <Text
      ref={ref}
      className={badgeTextStyle({
        parentVariants: {
          size: parentSize,
          action: parentAction,
        },
        size,
        class: className,
        bold,
      })}
      {...props}
    >
      {children}
    </Text>
  );
});

type IBadgeIconProps = React.ComponentPropsWithoutRef<typeof PrimitiveIcon> &
  VariantProps<typeof badgeIconStyle>;

const BadgeIcon = React.forwardRef<
  React.ComponentRef<typeof Svg>,
  IBadgeIconProps
>(function BadgeIcon(
  { className, size, hidden, fillAction = false, ...props },
  ref,
) {
  const { size: parentSize, action: parentAction } = useStyleContext(SCOPE);

  if (typeof size === "number") {
    return (
      <UIIcon
        ref={ref}
        {...props}
        className={badgeIconStyle({ class: className, hidden, fillAction })}
        size={size}
      />
    );
  } else if (
    (props?.height !== undefined || props?.width !== undefined) &&
    size === undefined
  ) {
    return (
      <UIIcon
        ref={ref}
        {...props}
        className={badgeIconStyle({ class: className, hidden, fillAction })}
      />
    );
  }
  const sizeDict = {
    sm: 12,
    md: 18,
    lg: 20,
    xl: 24,
    "2xl": 28,
  };
  return (
    <UIIcon
      className={badgeIconStyle({
        parentVariants: {
          action: parentAction,
        },
        class: className,
        hidden,
        fillAction,
      })}
      {...props}
      size={sizeDict[(size ?? parentSize) as keyof typeof sizeDict]}
      ref={ref}
    />
  );
});

Badge.displayName = "Badge";
BadgeText.displayName = "BadgeText";
BadgeIcon.displayName = "BadgeIcon";

export { Badge, BadgeIcon, BadgeText };
